import styled from '@emotion/styled';
import { format, isValid, parse } from 'date-fns';
import { FieldProps, getIn } from 'formik';
import _ from 'lodash';
import * as React from 'react';
import { formatDateForBackend } from '../../../../services/primitives/date-utils';
import { BaseInput, DatePicker, IBaseInputProps } from '../../';

/** Wall-clock form value: date only, or date + hours/minutes. */
export const DATE_TIME_FIELD_FORMAT = 'yyyy-MM-dd HH:mm';
const DATE_ONLY_FORMAT = 'yyyy-MM-dd';
const DATE_TIME_WITH_SECONDS_FORMAT = 'yyyy-MM-dd HH:mm:ss';
const TIME_INPUT_FORMAT = 'HH:mm';

const Container = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
`;

const DateField = styled.div`
    flex: 1;
    min-width: 0;

    /* Match DatePickerField hit-area: full-width calendar input */
    > span {
        display: block;
        width: 100%;
    }
`;

const TimeField = styled.div`
    flex: 0 0 120px;

    /* Match DatePickerField hover/click affordance on the time control */
    input[type='time'] {
        cursor: pointer;

        &::-webkit-calendar-picker-indicator {
            cursor: pointer;
        }
    }
`;

type IDateTimePickerFieldProps = IBaseInputProps &
    FieldProps & {
        format?: string;
    };

function parseDateTimeValue(value: unknown, dateTimeFormat: string): Date | undefined {
    if (_.isDate(value) && isValid(value)) {
        return value;
    }

    if (typeof value !== 'string' || !value.trim()) {
        return undefined;
    }

    const trimmed = value.trim();
    const formats = [dateTimeFormat, DATE_TIME_WITH_SECONDS_FORMAT, DATE_TIME_FIELD_FORMAT, DATE_ONLY_FORMAT];

    for (const fmt of formats) {
        const parsed = parse(trimmed, fmt, new Date());
        if (isValid(parsed) && format(parsed, fmt) === trimmed) {
            return parsed;
        }
    }

    return undefined;
}

function toTimeInputValue(date: Date | undefined, hasExplicitTime: boolean): string {
    if (!date || !hasExplicitTime) {
        return '';
    }
    return format(date, TIME_INPUT_FORMAT);
}

function hasExplicitTimePart(value: unknown): boolean {
    return typeof value === 'string' && /\d{1,2}:\d{2}/.test(value);
}

function normalizeTimeInput(timeValue: string): { hours: number; minutes: number } | undefined {
    const match = timeValue.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!match) {
        return undefined;
    }
    return {
        hours: Number(match[1]),
        minutes: Number(match[2]),
    };
}

/**
 * Combined date + time Formik field (hours and minutes only).
 * Stores wall-clock strings (`yyyy-MM-dd` or `yyyy-MM-dd HH:mm`) — no UTC conversion.
 * Submit path pads time to HH:mm:ss for the API.
 */
export class DateTimePickerField extends React.PureComponent<IDateTimePickerFieldProps> {
    public render(): React.ReactElement {
        const {
            field,
            form: { errors, touched },
            format: formatProp,
            ...props
        } = this.props;
        const dateTimeFormat = formatProp || DATE_TIME_FIELD_FORMAT;
        const date = parseDateTimeValue(field.value, dateTimeFormat);
        const error = getIn(errors, field.name);
        const isTouched = getIn(touched, field.name);
        const errorMessage: string | undefined = isTouched && typeof error === 'string' ? error : undefined;

        return (
            <Container>
                <DateField>
                    <DatePicker
                        {...props}
                        name={field.name}
                        errorMessage={errorMessage}
                        onDateSelected={this.handleDateChange}
                        value={date ? formatDateForBackend(date) || '' : ''}
                        format={DATE_ONLY_FORMAT}
                    />
                </DateField>
                <TimeField>
                    <BaseInput
                        type="time"
                        step={60}
                        name={`${field.name}Time`}
                        value={toTimeInputValue(date, hasExplicitTimePart(field.value))}
                        onChange={this.handleTimeChange}
                        onBlur={field.onBlur}
                        disabled={props.disabled}
                        aria-label={`${field.name} time`}
                    />
                </TimeField>
            </Container>
        );
    }

    private readonly handleDateChange = (value: Date | undefined): void => {
        const {
            field,
            form: { setFieldValue, setFieldTouched },
            format: formatProp,
        } = this.props;
        const dateTimeFormat = formatProp || DATE_TIME_FIELD_FORMAT;

        if (!value) {
            setFieldValue(field.name, '');
            setFieldTouched(field.name, true, false);
            return;
        }

        const dateOnly = formatDateForBackend(value);
        if (!dateOnly) {
            setFieldValue(field.name, '');
            setFieldTouched(field.name, true, false);
            return;
        }

        const existing = parseDateTimeValue(field.value, dateTimeFormat);
        if (existing && hasExplicitTimePart(field.value)) {
            const next = new Date(
                value.getFullYear(),
                value.getMonth(),
                value.getDate(),
                existing.getHours(),
                existing.getMinutes(),
                0,
            );
            setFieldValue(field.name, format(next, dateTimeFormat));
        } else {
            setFieldValue(field.name, dateOnly);
        }
        setFieldTouched(field.name, true, false);
    };

    private readonly handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const {
            field,
            form: { setFieldValue, setFieldTouched },
            format: formatProp,
        } = this.props;
        const dateTimeFormat = formatProp || DATE_TIME_FIELD_FORMAT;
        const timeValue = e.target.value;
        const existing = parseDateTimeValue(field.value, dateTimeFormat);

        if (!timeValue) {
            if (existing) {
                setFieldValue(field.name, formatDateForBackend(existing) || '');
            } else {
                setFieldValue(field.name, '');
            }
            setFieldTouched(field.name, true, false);
            return;
        }

        const parsedTime = normalizeTimeInput(timeValue);
        if (!parsedTime) {
            return;
        }

        const base = existing ?? new Date();
        const next = new Date(
            base.getFullYear(),
            base.getMonth(),
            base.getDate(),
            parsedTime.hours,
            parsedTime.minutes,
            0,
        );
        setFieldValue(field.name, format(next, dateTimeFormat));
        setFieldTouched(field.name, true, false);
    };
}
